import React from "react";
import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  AnySourceType,
  SOURCE_TYPE_URL_MAP,
} from "constants/source-type.constants";
import {
  PaymentSourceLabelMap,
  PaymentSourceType,
  PaymentStatus,
  paymentStatusMap,
} from "features/Payments/constants/payment.constants";
import { formatCurrency } from "utils/currency";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import PaymentDirectionBadge from "../PaymentDirectionBadge";
import { usePaymentOverview } from "./PaymentOverviewContext";
import { ClientInfoCard } from "components/common/ClientInfoCard";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import type { Payment } from "../types/payment.types";
import {
  resolvePaymentSourceCode,
  resolvePaymentSourceLinkId,
  resolvePaymentPayeeName,
} from "../utils/paymentSource";

function buildPaymentTimeline(payment: Payment): TimelineStep[] {
  const history = payment.statusHistory ?? [];
  if (history.length === 0) {
    const s = payment.status as PaymentStatus;
    const label = paymentStatusMap[s]?.label ?? payment.status;
    const state: TimelineStepState = s === PaymentStatus.CANCELLED ? "skipped" : "done";
    return [{ label, timestamp: payment.created_at, state, color: paymentStatusMap[s]?.color }];
  }
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const s = entry.status as PaymentStatus;
    const label = paymentStatusMap[s]?.label ?? entry.status;
    const state: TimelineStepState = isLast
      ? s === PaymentStatus.CANCELLED ? "skipped" : "done"
      : "done";
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null ? `#${entry.by}` : undefined;
    return {
      label,
      timestamp: entry.at,
      by: byLabel,
      actor: entry.message ?? undefined,
      state,
      color: paymentStatusMap[s]?.color,
    };
  });
}

const OverviewTab = () => {
  const { payment } = usePaymentOverview();

  if (!payment) return null;

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Payment info</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th
                        className="ps-0"
                        scope="row"
                        style={{ width: "180px" }}
                      >
                        Code :
                      </th>
                      <td className="text-muted">{payment.code}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Amount :
                      </th>
                      <td className="text-muted">
                        {formatCurrency(payment.amount)}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Payment date :
                      </th>
                      <td className="text-muted">
                        {payment.paymentDate ? (
                          <DateOnly date={payment.paymentDate} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Mode of payment :
                      </th>
                      <td className="text-muted">
                        {payment.modeOfPayment?.name ?? "—"}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Reference number :
                      </th>
                      <td className="text-muted">
                        {payment.referenceNumber?.trim() || "—"}
                      </td>
                    </tr>
                    {payment.client ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Client :
                        </th>
                        <td className="text-muted">
                          <Link
                            to={`/clients/${payment.clientId}`}
                            className="link fw-medium"
                          >
                            {getClientDisplayName(payment.client)}
                          </Link>
                        </td>
                      </tr>
                    ) : null}
                    {payment.payee && payment.payeeId ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Paid to :
                        </th>
                        <td className="text-muted">
                          <Link
                            to={`/employees/${payment.payeeId}`}
                            className="link fw-medium"
                          >
                            {resolvePaymentPayeeName(payment)}
                          </Link>
                        </td>
                      </tr>
                    ) : null}
                    <tr>
                      <th className="ps-0" scope="row">
                        Cash flow direction :
                      </th>
                      <td>
                        <PaymentDirectionBadge record={payment} />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Source :
                      </th>
                      <td>
                        {payment.sourceType ? (
                          <div>
                            <div className="text-muted small">
                              {PaymentSourceLabelMap[
                                payment.sourceType as PaymentSourceType
                              ]}
                            </div>
                            {(() => {
                              const linkId = resolvePaymentSourceLinkId(payment);
                              if (linkId == null) return null;
                              return (
                                <Link
                                  to={`${
                                    SOURCE_TYPE_URL_MAP[
                                      payment.sourceType as AnySourceType
                                    ]
                                  }/${linkId}`}
                                  className="link fw-medium"
                                >
                                  {resolvePaymentSourceCode(payment) ?? `#${linkId}`}
                                </Link>
                              );
                            })()}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {payment.remarks?.trim() ? (
                <div className="pt-3 border-top border-top-dashed mt-4">
                  <h6 className="mb-2 text-uppercase">Remarks</h6>
                  <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {payment.remarks.trim()}
                  </p>
                </div>
              ) : null}

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  {payment.created_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={payment.created_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}

                  {payment.updated_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">
                          Last updated
                        </p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={payment.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}

                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status :</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge
                          value={payment.status}
                          map={paymentStatusMap}
                        />
                      </h5>
                    </div>
                  </Col>

                  {payment.deleted_at != null && payment.deleted_at !== "" ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">
                          Deleted
                        </p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={payment.deleted_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>

      <Col lg={4}>
        <Card>
          <CardBody>
            <h6 className="text-uppercase mb-4 text-muted">Timeline</h6>
            <Timeline steps={buildPaymentTimeline(payment)} />
          </CardBody>
        </Card>
        {payment.client && payment.clientId ? (
          <ClientInfoCard client={payment.client} clientId={payment.clientId} />
        ) : null}
      </Col>
    </Row>
  );
};

export default OverviewTab;
